import { useState } from "react";
import { Link } from "react-router-dom";
import AppHeader from "@/components/feature/AppHeader";
import AppFooter from "@/components/feature/AppFooter";
import CodeBlock from "@/components/base/CodeBlock";
import ApiDebugPanel from "@/pages/docs/integration/components/ApiDebugPanel";

const ENDPOINT = `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/license-validate`;
const PUBLIC_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;

const REQUEST_FIELDS = [
  { name: "licenseKey", required: "必填", type: "string", desc: "客户持有的许可证密钥，大小写不敏感。" },
  { name: "machineCode", required: "必填", type: "string", desc: "客户端根据本机硬件算出的机器码，用于绑定机器。" },
  { name: "deviceName", required: "可选", type: "string", desc: "设备名称，便于在后台识别，缺省为「联网设备」。" },
  { name: "os", required: "可选", type: "string", desc: "操作系统描述，例如 Windows 11。" },
  { name: "product", required: "可选", type: "string", desc: "当前软件名；填写后若与密钥所属产品不符会直接拒绝。" },
];

const RESPONSE_FIELDS = [
  { name: "valid", type: "boolean", desc: "授权是否有效，客户端以此字段决定是否放行。" },
  { name: "reason", type: "string", desc: "结果码，ok 表示成功，其余为失败原因（见下方错误码）。" },
  { name: "message", type: "string", desc: "可直接展示给用户的中文提示。" },
  { name: "productName / editionLabel", type: "string", desc: "产品名称与版本类型（如「标准版」）。" },
  { name: "seatCount / activationCount / maxActivations", type: "number", desc: "授权席位与已激活 / 最大激活数。" },
  { name: "openCount / maxOpens / remainingOpens", type: "number", desc: "已打开次数 / 上限 / 剩余次数，maxOpens 为 null 表示不限。" },
  { name: "expiresAt / remainingDays", type: "string / number", desc: "到期时间与剩余天数，remainingDays 为 null 表示永久。" },
  { name: "firstBind", type: "boolean", desc: "本次是否为首次绑定该机器（自动绑机）。" },
  { name: "checkedAt", type: "string", desc: "本次校验的服务器时间（ISO 格式）。" },
];

const REASONS = [
  { code: "ok", meaning: "授权有效，放行", tone: "success" },
  { code: "missing_api_key", meaning: "未提供 API Key（x-api-key）", tone: "error" },
  { code: "invalid_api_key", meaning: "API Key 无效", tone: "error" },
  { code: "key_revoked", meaning: "API Key 已被吊销", tone: "error" },
  { code: "client_suspended", meaning: "对接方已被暂停", tone: "error" },
  { code: "key_expired", meaning: "API Key 已过期", tone: "error" },
  { code: "rate_limited", meaning: "超过每分钟限流（429）", tone: "warning" },
  { code: "quota_exceeded", meaning: "超过每日调用配额（429）", tone: "warning" },
  { code: "license_not_found", meaning: "密钥无效或不存在", tone: "error" },
  { code: "product_mismatch", meaning: "密钥不属于当前软件", tone: "error" },
  { code: "revoked", meaning: "许可证已被吊销", tone: "error" },
  { code: "suspended", meaning: "许可证已被暂停", tone: "error" },
  { code: "expired", meaning: "许可证已过期", tone: "error" },
  { code: "seat_limit", meaning: "激活名额已满，需先解绑设备", tone: "warning" },
  { code: "open_limit", meaning: "已达到打开次数上限", tone: "warning" },
  { code: "missing_license_key", meaning: "未提供 licenseKey", tone: "error" },
  { code: "missing_machine_code", meaning: "未提供 machineCode", tone: "error" },
  { code: "method_not_allowed", meaning: "请求方法非 POST", tone: "warning" },
  { code: "server_error", meaning: "服务端异常，请稍后重试", tone: "error" },
];

const REQUEST_BODY = `{
  "licenseKey": "LH-XXXX-XXXX-XXXX-XXXX",
  "machineCode": "A1B2-C3D4-E5F6-7890",
  "deviceName": "张三的办公电脑",
  "os": "Windows 11",
  "product": "你的软件名"
}`;

const SUCCESS_BODY = `{
  "valid": true,
  "reason": "ok",
  "message": "授权有效",
  "productName": "你的软件名",
  "edition": "standard",
  "editionLabel": "标准版",
  "seatCount": 3,
  "activationCount": 2,
  "maxActivations": 3,
  "openCount": 12,
  "maxOpens": 100,
  "remainingOpens": 88,
  "expiresAt": "2027-01-01T00:00:00.000Z",
  "remainingDays": 200,
  "firstBind": false,
  "checkedAt": "2026-10-04T08:30:00.000Z"
}`;

const FAIL_BODY = `{
  "valid": false,
  "reason": "seat_limit",
  "message": "激活名额已满（3/3），请先解绑其它设备",
  "activationCount": 3,
  "maxActivations": 3
}`;

const CURL_EXAMPLE = `curl -X POST "${ENDPOINT}" \\
  -H "Content-Type: application/json" \\
  -H "apikey: ${PUBLIC_KEY}" \\
  -H "x-api-key: lh_live_你的API密钥" \\
  -d '{
    "licenseKey": "LH-XXXX-XXXX-XXXX-XXXX",
    "machineCode": "A1B2-C3D4-E5F6-7890",
    "deviceName": "张三的办公电脑",
    "os": "Windows 11",
    "product": "你的软件名"
  }'`;

const JS_EXAMPLE = `const ENDPOINT = "${ENDPOINT}";
const APIKEY = "${PUBLIC_KEY}";
const INTEGRATION_KEY = "lh_live_你的API密钥";

async function checkLicense(payload) {
  const res = await fetch(ENDPOINT, {
    method: "POST",
    headers: { "Content-Type": "application/json", apikey: APIKEY, "x-api-key": INTEGRATION_KEY },
    body: JSON.stringify({
      licenseKey: payload.licenseKey,
      machineCode: payload.machineCode,
      deviceName: payload.deviceName,
      os: payload.os,
      product: payload.product,
    }),
  });
  const data = await res.json();
  if (data.valid) {
    return { ok: true, data };
  }
  return { ok: false, reason: data.reason, message: data.message };
}`;

const CSHARP_EXAMPLE = `using System.Text;
using System.Text.Json;

var payload = new {
    licenseKey = "LH-XXXX-XXXX-XXXX-XXXX",
    machineCode = "A1B2-C3D4-E5F6-7890",
    deviceName = Environment.MachineName,
    os = Environment.OSVersion.ToString(),
    product = "你的软件名"
};

using var http = new HttpClient();
http.DefaultRequestHeaders.Add("apikey", "${PUBLIC_KEY}");
http.DefaultRequestHeaders.Add("x-api-key", "lh_live_你的API密钥");

var content = new StringContent(
    JsonSerializer.Serialize(payload), Encoding.UTF8, "application/json");
var res = await http.PostAsync("${ENDPOINT}", content);
var json = await res.Content.ReadAsStringAsync();
// 解析 json，读取 valid 字段决定是否放行`;

const PYTHON_EXAMPLE = `import requests

resp = requests.post(
    "${ENDPOINT}",
    headers={"apikey": "${PUBLIC_KEY}", "x-api-key": "lh_live_你的API密钥", "Content-Type": "application/json"},
    json={
        "licenseKey": "LH-XXXX-XXXX-XXXX-XXXX",
        "machineCode": "A1B2-C3D4-E5F6-7890",
        "deviceName": "PC-01",
        "os": "Windows 11",
        "product": "你的软件名",
    },
    timeout=8,
)
data = resp.json()
if data.get("valid"):
    print("授权有效，到期：", data.get("expiresAt"))
else:
    print("授权失败：", data.get("reason"), data.get("message"))`;

const OFFLINE_VERIFY = `// 离线一机一码验签参考实现（非对称：后台私钥签名 / 软件内置公钥验签）
// 可运行于 Node / Electron / 打包后的 EXE 内嵌 JS / 现代浏览器
const PUBLIC_JWK = {
  // 粘贴后台「授权工具 → 离线一机一码 → 离线验签公钥」复制的 JWK
  kty: "EC", crv: "P-256", x: "...", y: "...",
};
const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ234567";
const EPOCH = Date.UTC(2020, 0, 1);

function base32Decode(str) {
  let bits = 0, value = 0;
  const out = [];
  for (const ch of str.toUpperCase()) {
    const idx = ALPHABET.indexOf(ch);
    if (idx < 0) continue;
    value = (value << 5) | idx; bits += 5;
    if (bits >= 8) { out.push((value >>> (bits - 8)) & 0xff); bits -= 8; }
  }
  return new Uint8Array(out);
}

async function verifyOffline(machineCode, code) {
  const raw = code.replace(/-/g, "").toUpperCase();
  const expiryCode = raw.slice(0, 4);   // 前 4 位：有效期
  const sigStr = raw.slice(4);          // 其余：ECDSA 签名（base32）
  const key = await crypto.subtle.importKey(
    "jwk", PUBLIC_JWK, { name: "ECDSA", namedCurve: "P-256" }, false, ["verify"]);
  const message = new TextEncoder().encode(machineCode.toUpperCase() + "|" + expiryCode);
  const ok = await crypto.subtle.verify(
    { name: "ECDSA", hash: "SHA-256" }, key, base32Decode(sigStr), message);
  if (!ok) return { ok: false, reason: "signature" };
  if (expiryCode === "0000") return { ok: true, permanent: true };
  const days = parseInt(expiryCode, 36);
  const expiresAt = new Date(EPOCH + days * 86400000);
  return { ok: Date.now() <= expiresAt.getTime(), expiresAt };
}`;

const CODE_TABS: { key: string; label: string; code: string; lang: string }[] = [
  { key: "curl", label: "cURL", code: CURL_EXAMPLE, lang: "bash" },
  { key: "js", label: "JavaScript / TypeScript", code: JS_EXAMPLE, lang: "javascript" },
  { key: "csharp", label: "C# (.NET)", code: CSHARP_EXAMPLE, lang: "csharp" },
  { key: "python", label: "Python", code: PYTHON_EXAMPLE, lang: "python" },
];

const OFFLINE_STEPS = [
  { title: "取公钥", text: "在「管理后台 → 授权工具 → 离线一机一码」复制离线验签公钥，内置到打包软件中。" },
  { title: "取机器码", text: "软件启动时根据 CPU 序列号 / 主板 UUID / 磁盘序列号等稳定信息算出机器码，展示或复制给客户。" },
  { title: "签字激活码", text: "作者在授权工具输入机器码（可选有效期），后台用私钥立即签出激活码（私钥不出库）。" },
  { title: "本地验签", text: "客户粘贴激活码，软件用内置公钥本地验签，无需联网即可判断授权是否有效。" },
];

const toneClass: Record<string, string> = {
  success: "bg-primary-100 text-primary-700",
  warning: "bg-accent-100 text-accent-700",
  error: "bg-secondary-100 text-secondary-900",
};

export default function IntegrationDocsPage() {
  const [codeTab, setCodeTab] = useState(CODE_TABS[0].key);
  const active = CODE_TABS.find((item) => item.key === codeTab) ?? CODE_TABS[0];

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <AppHeader />

      <main className="flex-1">
        <section className="border-b border-background-200 bg-background-100">
          <div className="mx-auto w-full max-w-5xl px-4 py-12 md:px-6 md:py-16">
            <nav className="flex items-center gap-1.5 text-xs text-foreground-500">
              <Link to="/" className="cursor-pointer transition-colors hover:text-foreground-950">
                首页
              </Link>
              <span className="flex h-3 w-3 items-center justify-center">
                <i className="ri-arrow-right-s-line" />
              </span>
              <span className="text-foreground-800">接入文档</span>
            </nav>
            <span className="mt-4 inline-flex items-center gap-2 rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-700">
              <span className="flex h-3 w-3 items-center justify-center">
                <i className="ri-book-2-line" />
              </span>
              给打包工具方 / 客户端开发者
            </span>
            <h1 className="mt-3 font-heading text-2xl font-semibold tracking-tight text-foreground-950 md:text-4xl">
              授权接入文档
            </h1>
            <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
              本文档面向把软件打包成 EXE / 桌面程序的开发者，说明如何接入「网络验证」接口做联网授权校验，
              以及如何用「离线一机一码」方案在不联网的环境下完成本地验签。
            </p>
          </div>
        </section>

        <section className="mx-auto w-full max-w-5xl space-y-10 px-4 py-10 md:px-6 md:py-14">
          <div>
            <h2 className="font-heading text-xl font-semibold text-foreground-950">一、网络验证接口</h2>
            <p className="mt-2 text-sm leading-relaxed text-foreground-600">
              软件启动时用「许可证密钥 + 机器码」调用本接口；后端会依次校验状态、到期时间、激活名额与打开次数，
              并在名额内自动完成机器绑定。客户端只需判断返回的 <strong>valid</strong> 字段。
            </p>

            <div className="mt-5 grid gap-4 sm:grid-cols-2">
              <div className="rounded-lg border border-background-200 bg-background-50 p-4">
                <p className="text-xs text-foreground-500">请求方式</p>
                <p className="mt-1 font-mono text-sm text-foreground-950">POST</p>
              </div>
              <div className="rounded-lg border border-background-200 bg-background-50 p-4">
                <p className="text-xs text-foreground-500">请求头</p>
                <p className="mt-1 font-mono text-sm text-foreground-950">Content-Type: application/json</p>
                <p className="mt-1 font-mono text-xs text-foreground-600">apikey: 你项目 .env 里的公开 anon key</p>
                <p className="mt-1 font-mono text-xs text-foreground-600">x-api-key: 对接方管理里签发的 API 密钥（必填）</p>
              </div>
            </div>

            <div className="mt-4 flex items-start gap-2.5 rounded-lg border border-primary-200 bg-primary-50 p-4">
              <span className="flex h-5 w-5 shrink-0 items-center justify-center text-primary-700">
                <i className="ri-key-2-line" />
              </span>
              <p className="text-xs leading-relaxed text-foreground-700">
                本接口按 API Key 鉴权：请先在「管理后台 → 对接方管理」为你的应用签发一把密钥，并在每次请求携带请求头
                <span className="mx-1 font-mono">x-api-key: 你的密钥</span>。每把密钥可单独配置限流（次/分钟）与每日配额，超限会返回 429。
              </p>
            </div>

            <div className="mt-4 rounded-lg border border-background-200 bg-secondary-50 p-4">
              <p className="text-xs text-foreground-500">接口地址</p>
              <p className="mt-1 break-all font-mono text-sm text-foreground-900">{ENDPOINT}</p>
            </div>
          </div>

          <div>
            <h3 className="font-heading text-base font-semibold text-foreground-950">请求体字段</h3>
            <div className="mt-3 overflow-x-auto rounded-lg border border-background-200">
              <table className="w-full min-w-[640px] text-sm">
                <thead>
                  <tr className="bg-background-100 text-left text-xs font-medium text-foreground-600">
                    <th className="px-4 py-3">字段</th>
                    <th className="px-4 py-3">必填</th>
                    <th className="px-4 py-3">类型</th>
                    <th className="px-4 py-3">说明</th>
                  </tr>
                </thead>
                <tbody>
                  {REQUEST_FIELDS.map((field) => (
                    <tr key={field.name} className="border-t border-background-200 bg-background-50">
                      <td className="px-4 py-3 font-mono text-[13px] text-foreground-950">{field.name}</td>
                      <td className="px-4 py-3">
                        <span
                          className={`rounded-full px-2 py-0.5 text-[11px] ${
                            field.required === "必填" ? "bg-accent-100 text-accent-700" : "bg-background-200 text-foreground-600"
                          }`}
                        >
                          {field.required}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-foreground-600">{field.type}</td>
                      <td className="px-4 py-3 text-foreground-700">{field.desc}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="grid gap-5 lg:grid-cols-2">
            <div>
              <h3 className="font-heading text-base font-semibold text-foreground-950">请求示例</h3>
              <div className="mt-3">
                <CodeBlock label="POST body" code={REQUEST_BODY} />
              </div>
            </div>
            <div>
              <h3 className="font-heading text-base font-semibold text-foreground-950">成功返回</h3>
              <div className="mt-3">
                <CodeBlock label="200 OK" code={SUCCESS_BODY} />
              </div>
            </div>
          </div>

          <div>
            <h3 className="font-heading text-base font-semibold text-foreground-950">失败返回（示例）</h3>
            <p className="mt-2 text-sm leading-relaxed text-foreground-600">
              校验失败时同样返回 200，<strong>valid 为 false</strong> 并附带 reason 与中文提示，客户端据此展示对应文案。
            </p>
            <div className="mt-3">
              <CodeBlock label="200 OK · valid:false" code={FAIL_BODY} />
            </div>
          </div>

          <div>
            <h3 className="font-heading text-base font-semibold text-foreground-950">返回字段</h3>
            <div className="mt-3 overflow-x-auto rounded-lg border border-background-200">
              <table className="w-full min-w-[640px] text-sm">
                <thead>
                  <tr className="bg-background-100 text-left text-xs font-medium text-foreground-600">
                    <th className="px-4 py-3">字段</th>
                    <th className="px-4 py-3">类型</th>
                    <th className="px-4 py-3">说明</th>
                  </tr>
                </thead>
                <tbody>
                  {RESPONSE_FIELDS.map((field) => (
                    <tr key={field.name} className="border-t border-background-200 bg-background-50">
                      <td className="px-4 py-3 font-mono text-[13px] text-foreground-950">{field.name}</td>
                      <td className="px-4 py-3 text-foreground-600">{field.type}</td>
                      <td className="px-4 py-3 text-foreground-700">{field.desc}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <ApiDebugPanel endpoint={ENDPOINT} apiKey={PUBLIC_KEY} />

          <div>
            <h3 className="font-heading text-base font-semibold text-foreground-950">结果码（reason）</h3>
            <div className="mt-3 grid gap-2 sm:grid-cols-2">
              {REASONS.map((item) => (
                <div
                  key={item.code}
                  className="flex items-center justify-between gap-3 rounded-lg border border-background-200 bg-background-50 px-4 py-3"
                >
                  <span className="font-mono text-[13px] text-foreground-950">{item.code}</span>
                  <span className={`rounded-full px-2.5 py-0.5 text-[11px] ${toneClass[item.tone]}`}>{item.meaning}</span>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h3 className="font-heading text-base font-semibold text-foreground-950">调用示例</h3>
            <div className="mt-3 mb-4 inline-flex flex-wrap rounded-full border border-background-300 bg-background-100 p-1">
              {CODE_TABS.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setCodeTab(tab.key)}
                  className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                    codeTab === tab.key ? "bg-background-50 text-foreground-950" : "text-foreground-600 hover:text-foreground-900"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            <CodeBlock label={active.label} code={active.code} />
          </div>

          <div className="border-t border-background-200 pt-10">
            <h2 className="font-heading text-xl font-semibold text-foreground-950">二、离线一机一码方案</h2>
            <p className="mt-2 text-sm leading-relaxed text-foreground-600">
              面向完全无法联网的运行环境：由后台用私钥对「机器码」离线签名生成激活码，软件内置公钥在本地验签。
              私钥只存于后台、永不外泄，激活码与机器一一对应，无法在其它机器复用，也无法被篡改。
            </p>

            <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {OFFLINE_STEPS.map((step, index) => (
                <div key={step.title} className="rounded-lg border border-background-200 bg-background-50 p-4">
                  <span className="font-mono text-xs text-foreground-400">STEP {index + 1}</span>
                  <h4 className="mt-2 text-sm font-semibold text-foreground-950">
                    <a href="#offline" className="hover:text-primary-700">
                      {step.title}
                    </a>
                  </h4>
                  <p className="mt-1 text-xs leading-relaxed text-foreground-600">{step.text}</p>
                </div>
              ))}
            </div>

            <div className="mt-5 rounded-lg border border-background-200 bg-background-100 p-4">
              <p className="text-sm font-medium text-foreground-950">激活码结构</p>
              <p className="mt-2 break-all font-mono text-sm text-foreground-900">XXXX-XXXX-…-XXXX（有效期前缀 + 签名）</p>
              <ul className="mt-3 space-y-1.5 text-xs leading-relaxed text-foreground-600">
                <li>· 前 4 位：有效期前缀（base36 天数，自 2020-01-01 起算；<span className="font-mono">0000</span> 表示永久有效）。</li>
                <li>· 其余：对「机器码 + 有效期前缀」做 ECDSA P-256 / SHA-256 签名后的 base32 编码，用于本地验签。</li>
                <li>· 机器码大小写不敏感，校验时统一转为大写。因含签名，激活码较长属正常现象。</li>
              </ul>
            </div>

            <h3 className="mt-6 font-heading text-base font-semibold text-foreground-950">离线验签参考实现</h3>
            <div className="mt-3">
              <CodeBlock label="verifyOffline.js" code={OFFLINE_VERIFY} />
            </div>

            <div className="mt-4 flex items-start gap-2.5 rounded-lg border border-primary-200 bg-primary-50 p-4">
              <span className="flex h-5 w-5 shrink-0 items-center justify-center text-primary-700">
                <i className="ri-shield-check-line" />
              </span>
              <p className="text-xs leading-relaxed text-foreground-700">
                本方案已采用「非对称验签」：后台私钥签名、软件内置公钥验签。即使客户端被逆向，公开的公钥也无法伪造激活码，
                防篡改能力明显强于对称密钥方案。私钥请妥善保管，仅存于后台。
              </p>
            </div>
          </div>
        </section>
      </main>

      <AppFooter />
    </div>
  );
}